/** @jsx jsx */

import { ChangeEvent, Fragment } from 'react';

import {
  Alert,
  AlertDescription,
  Field,
  Grid,
  Margin,
  Radio,
  RadioGroup,
  Tag,
  Text,
  TextInput,
  ToggleSwitch,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import DoubleDropdowns from 'app/developer-v3/components/DoubleDropdowns';
import RangeSlider from 'app/developer-v3/components/RangeSlider';
import { isLabeledVersion } from 'app/developer-v3/utils/version';
import { sortVersionStrings } from 'app/developer-v3/utils';

import { MIGRATION_EMAIL_TYPES, MIGRATION_TYPES } from './constants';

import type { MigrationEmailType, MigrationType } from './types';

const Styles = {
  percentageLabel: css`
    white-space: nowrap;
  `,
  wrapper: css`
    margin: 20px 0px;
  `,
};

type Props = {
  email: string;
  emailType: MigrationEmailType;
  fromVersion?: string;
  numUsers: number;
  onChangeEmail: (value: string) => void;
  onChangePercentage: (value: number) => void;
  onChangeType: (value: MigrationType) => void;
  onChangeEmailType: (value: MigrationEmailType) => void;
  onSetFromVersion: (value: { value: string }) => void;
  onSetToVersion: (value: { value: string }) => void;
  percentage: number;
  toVersion?: string;
  type: MigrationType;
  versions: string[];
};

function UserMigration({
  email,
  emailType,
  fromVersion,
  numUsers,
  onChangeEmail,
  onChangePercentage,
  onChangeType: _onChangeType,
  onChangeEmailType,
  onSetFromVersion,
  onSetToVersion,
  percentage,
  toVersion,
  type,
  versions,
}: Props) {
  const isPercentage = type === MIGRATION_TYPES.percentage;
  const isEmail = type === MIGRATION_TYPES.email;
  const isUserEmail = emailType === MIGRATION_EMAIL_TYPES.user;

  const items = sortVersionStrings(versions)
    .filter(v => !isLabeledVersion(v))
    .map(v => ({ value: v, label: v }));

  return (
    <Fragment>
      <DoubleDropdowns
        leftDropdown={{
          title: 'From Version',
          items,
          value: fromVersion,
          onSetValue: onSetFromVersion,
        }}
        rightDropdown={{
          title: 'To Version',
          items,
          value: versions.includes(toVersion) ? toVersion : '',
          onSetValue: onSetToVersion,
        }}
      />
      <div css={Styles.wrapper}>
        <Text margin="20px 0px" tag="h3" type="Title">
          How would you like to migrate users?
        </Text>
        <RadioGroup
          aria-label="How would you like to migrate users?"
          value={type}
          onChange={(value: MigrationType) => _onChangeType(value)}
        >
          <Radio value="percentage">By percentage</Radio>
          <Radio value="email">By email</Radio>
        </RadioGroup>
        <Grid alignItems="center" columns="1fr auto" gap={20}>
          <div />
          {isEmail && (
            <Grid alignItems="center" columns="repeat(3, max-content)" gap={10}>
              <Text>Individual</Text>
              <ToggleSwitch
                aria-label="Toggle between migrating an individual user or an organization user"
                checked={emailType === MIGRATION_EMAIL_TYPES.account}
                onChange={() =>
                  onChangeEmailType(
                    MIGRATION_EMAIL_TYPES[isUserEmail ? 'account' : 'user']
                  )
                }
              />
              <Text>Organization</Text>
            </Grid>
          )}
        </Grid>
        {isPercentage ? (
          <Text margin="20px 0px" tag="p">
            Migrate a percentage of users.
          </Text>
        ) : isUserEmail ? (
          <Text margin="20px 0px" tag="p">
            Migrate one individual user at a time.
          </Text>
        ) : (
          <Text margin="20px 0px" tag="p">
            Migrate one organization user at a time.
          </Text>
        )}
        {isEmail &&
          (isUserEmail ? (
            <Margin margin="20px 0px">
              <Alert title="Zaps to migrate">
                <AlertDescription maxLines={5}>
                  Only the user's private Zaps under the user's individual
                  account will be migrated with this method. Zaps that are
                  shared across the user's organization accounts will not be
                  migrated.
                </AlertDescription>
              </Alert>
            </Margin>
          ) : (
            <Margin margin="20px 0px">
              <Alert status="warning" title="Zaps to migrate">
                <AlertDescription maxLines={5}>
                  Both private Zaps under the user's individual account and Zaps
                  shared across the user's organization accounts will be
                  migrated using this method. Please use with caution, as it may
                  break shared Zaps for other members in those organization
                  accounts.
                </AlertDescription>
              </Alert>
            </Margin>
          ))}
        {isPercentage ? (
          <Grid alignItems="center" columns="2fr 0.5fr" gap={20}>
            <RangeSlider
              max={100}
              min={5}
              onChange={onChangePercentage}
              step={1}
              value={percentage}
            />
            <Tag>
              <span css={Styles.percentageLabel}>
                {`${percentage}% (~${Math.round(
                  (percentage / 100) * numUsers
                )} users)`}
              </span>
            </Tag>
          </Grid>
        ) : (
          <Field
            label="Email"
            renderInput={inputProps => (
              <TextInput
                {...inputProps}
                aria-label="A user’s email to migrate"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onChangeEmail(event.target.value)
                }
                placeholder="example@example.com"
                value={email}
              />
            )}
          />
        )}
      </div>
    </Fragment>
  );
}

UserMigration.defaultProps = {
  email: '',
  emailType: MIGRATION_EMAIL_TYPES.user,
  percentage: 100,
};

export default UserMigration;
